import { useEffect, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import MeasuredChart from "@/components/MeasuredChart";
import {
  Package, Warehouse, ArrowUpRight, ArrowDownRight, CurrencyEur, TrendUp, Download, Buildings, Warning,
} from "@phosphor-icons/react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-neutral-200 p-6" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="label-eyebrow">{label}</div>
        <div className={`w-8 h-8 flex items-center justify-center ${accent || "bg-neutral-100 text-neutral-700"}`}>
          <Icon size={16} weight="bold" />
        </div>
      </div>
      <div className="font-display font-black text-3xl tracking-tighter">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [lowStock, setLowStock] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, w, ls] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/inventory/by-warehouse"),
          api.get("/alerts/low-stock"),
        ]);
        setStats(s.data);
        setWarehouses(w.data);
        setLowStock(ls.data);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const download = async (path, filename) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}${path}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Errore export");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export completato");
    } catch (e) {
      toast.error("Errore durante l'export");
    }
  };

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle="Panoramica del magazzino in tempo reale"
        action={
          <div className="flex gap-2">
            <button
              data-testid="export-inventory-btn"
              onClick={() => download("/export/inventory", "giacenza.xlsx")}
              className="text-sm px-4 py-2.5 border border-neutral-300 bg-white hover:bg-neutral-50 transition-colors duration-150 flex items-center gap-2"
            >
              <Download size={16} /> Giacenza XLSX
            </button>
            <button
              data-testid="export-movements-btn"
              onClick={() => download("/export/movements", "movimenti.xlsx")}
              className="text-sm px-4 py-2.5 bg-[#002FA7] hover:bg-[#002480] text-white transition-colors duration-150 flex items-center gap-2"
            >
              <Download size={16} /> Movimenti XLSX
            </button>
          </div>
        }
      />

      <div className="p-8 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-0 -mx-px">
          <Stat icon={Package} label="Prodotti" value={stats?.total_products ?? "—"} />
          <Stat icon={Buildings} label="Magazzini" value={stats?.total_warehouses ?? "—"} accent="bg-neutral-900 text-white" />
          <Stat icon={Warehouse} label="In Giacenza" value={stats?.total_in_stock ?? "—"} accent="bg-[#002FA7] text-white" />
          <Stat icon={ArrowUpRight} label="Usciti" value={stats?.total_out ?? "—"} accent="bg-red-50 text-red-700" />
          <Stat icon={TrendUp} label="Movimenti" value={stats?.total_movements ?? "—"} />
          <Stat icon={CurrencyEur} label="Valore" value={stats ? `€ ${Number(stats.total_value).toLocaleString("it-IT", { maximumFractionDigits: 0 })}` : "—"} accent="bg-emerald-50 text-emerald-700" />
        </div>

        {/* Warehouse breakdown */}
        {warehouses.length > 0 && (
          <div className="bg-white border border-neutral-200 p-6">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="label-eyebrow mb-1">Ripartizione</div>
                <h3 className="font-display font-bold text-xl tracking-tight">Giacenza per magazzino</h3>
              </div>
              <span className="text-xs text-neutral-500">{warehouses.length} location</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {warehouses.map((w) => (
                <div key={w.warehouse_id} className="border border-neutral-200 p-3" data-testid={`dash-wh-${w.warehouse_id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-6 h-6 flex items-center justify-center ${w.type === "physical" ? "bg-[#002FA7] text-white" : "bg-amber-500 text-white"}`}>
                      {w.type === "physical" ? <Buildings size={12} weight="bold" /> : <TrendUp size={12} weight="bold" />}
                    </div>
                    <div className="text-xs text-neutral-500 truncate">{w.type === "physical" ? "Fisico" : "Mobile"}</div>
                  </div>
                  <div className="text-sm font-medium truncate">{w.name}</div>
                  <div className="font-display font-black text-2xl tracking-tighter mt-1">{w.stock}</div>
                  <div className="text-xs text-neutral-500 font-mono">€ {Number(w.value).toLocaleString("it-IT", { maximumFractionDigits: 0 })}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white border border-neutral-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="label-eyebrow mb-1">Ultimi 14 giorni</div>
                <h3 className="font-display font-bold text-xl tracking-tight">Movimenti nel tempo</h3>
              </div>
            </div>
            <MeasuredChart height={280}>
              {({ width, height }) => (
                <LineChart width={width} height={height} data={stats?.timeline || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#737373" }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 10, fill: "#737373" }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: "#fff", border: "1px solid #e5e5e5", borderRadius: 0, fontSize: 12 }}
                  />
                  <Line type="monotone" dataKey="entry" name="Entrate" stroke="#16A34A" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="exit" name="Uscite" stroke="#DC2626" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="return" name="Resi" stroke="#EAB308" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="transfer" name="Trasferimenti" stroke="#2563EB" strokeWidth={2} dot={false} />
                </LineChart>
              )}
            </MeasuredChart>
            <div className="flex flex-wrap gap-4 mt-4 text-xs text-neutral-600">
              <span className="flex items-center gap-2"><span className="w-3 h-0.5 bg-[#16A34A]" />Entrate</span>
              <span className="flex items-center gap-2"><span className="w-3 h-0.5 bg-[#DC2626]" />Uscite</span>
              <span className="flex items-center gap-2"><span className="w-3 h-0.5 bg-[#EAB308]" />Resi</span>
              <span className="flex items-center gap-2"><span className="w-3 h-0.5 bg-[#2563EB]" />Trasferimenti</span>
            </div>
          </div>

          <div className="bg-white border border-neutral-200 p-6">
            <div className="mb-6">
              <div className="label-eyebrow mb-1">Top 5</div>
              <h3 className="font-display font-bold text-xl tracking-tight">Prodotti in giacenza</h3>
            </div>
            {(stats?.top_products || []).length === 0 ? (
              <div className="text-sm text-neutral-500 py-12 text-center">Nessun dato ancora</div>
            ) : (
              <MeasuredChart height={280}>
                {({ width, height }) => (
                  <BarChart width={width} height={height} data={stats?.top_products || []} layout="vertical" margin={{ left: 10 }}>
                    <XAxis type="number" tick={{ fontSize: 10, fill: "#737373" }} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10, fill: "#404040" }} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e5e5e5", borderRadius: 0, fontSize: 12 }} />
                    <Bar dataKey="stock" fill="#002FA7" />
                  </BarChart>
                )}
              </MeasuredChart>
            )}
          </div>
        </div>

        {/* Low Stock Alerts */}
        <div className="bg-white border border-neutral-200" data-testid="low-stock-widget">
          <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-amber-100 text-amber-700 flex items-center justify-center">
                <Warning size={16} weight="bold" />
              </div>
              <div>
                <div className="label-eyebrow">Alert</div>
                <h3 className="font-display font-bold text-lg tracking-tight">Scorta sotto la soglia minima</h3>
              </div>
            </div>
            <span className="font-mono text-2xl font-black tracking-tighter text-amber-700">{lowStock.length}</span>
          </div>
          {lowStock.length === 0 ? (
            <div className="p-8 text-center text-sm text-neutral-500">
              Tutti i prodotti sono sopra la scorta minima configurata.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr className="text-left">
                    <th className="px-4 py-2 label-eyebrow">Prodotto</th>
                    <th className="px-4 py-2 label-eyebrow">SKU</th>
                    <th className="px-4 py-2 label-eyebrow text-right">Giacenza</th>
                    <th className="px-4 py-2 label-eyebrow text-right">Scorta Min.</th>
                    <th className="px-4 py-2 label-eyebrow">Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStock.map((r) => (
                    <tr key={r.product_id} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors">
                      <td className="px-4 py-2 font-medium">{r.name}</td>
                      <td className="px-4 py-2 font-mono text-xs text-neutral-600">{r.sku}</td>
                      <td className={`px-4 py-2 text-right font-mono font-bold ${r.stock === 0 ? "text-red-600" : "text-amber-700"}`}>{r.stock}</td>
                      <td className="px-4 py-2 text-right font-mono text-neutral-500">{r.min_stock}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${r.severity === "critical" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>
                          {r.severity === "critical" ? "Esaurito" : "Sotto soglia"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
